import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { surfbreaks, type Surfbreak } from "@/data/surfbreaks";
import { fetchConditions, conditionRating } from "@/lib/surf";
import { BreakPicker } from "@/components/surf/BreakPicker";
import { ConditionsPanel } from "@/components/surf/ConditionsPanel";
import { HourlyForecast } from "@/components/surf/HourlyForecast";
import { DailyForecast } from "@/components/surf/DailyForecast";
import { GearRecommender } from "@/components/surf/GearRecommender";
import { SurfAlerts } from "@/components/surf/SurfAlerts";
import { ElNinoMap } from "@/components/surf/ElNinoMap";
import { CompetitionsList } from "@/components/surf/CompetitionsList";
import { Rankings } from "@/components/surf/Rankings";
import { Profile } from "@/components/surf/Profile";
import { HotSpots } from "@/components/surf/HotSpots";
import { ProGate } from '@/components/ProGate';
import { Loader2, Waves, Flame, Trophy, Medal, User, Sparkles, ChevronDown, Navigation, Bell } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useGeolocation } from "@/hooks/use-geolocation";
import { haversineKm } from "@/lib/surf";


const TABS = [
  { v: "report",   icon: Waves,    label: "Report"    },
  { v: "hotspots", icon: Sparkles, label: "Hot Spots" },
  { v: "enso",     icon: Flame,    label: "El Niño"   },
  { v: "comps",    icon: Trophy,   label: "Comps"     },
  { v: "rankings", icon: Medal,    label: "Top 10"    },
  { v: "profile",  icon: User,     label: "Profile"   },
];

const Index = () => {
  const [selected, setSelected] = useState<Surfbreak | null>(null);
  const [activeTab, setActiveTab] = useState(() =>
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("tab") === "profile"
      ? "profile"
      : "report"
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [conditionsExpanded, setConditionsExpanded] = useState(false);
  const [directionsOpen, setDirectionsOpen] = useState(false);

  const { coords, loading: locating, error: locationError, request } = useGeolocation();
  const [unit, setUnit] = useState<"km" | "mi">("km");

  // Auto-request geolocation on mount to auto-sort breaks by distance
  useEffect(() => {
    request();
  }, [request]);

  // Set selected to closest break when coords are available
  useEffect(() => {
    if (coords && !selected) {
      let closest = surfbreaks[0];
      let minDist = haversineKm(coords, closest);
      
      for (const brk of surfbreaks) {
        const dist = haversineKm(coords, brk);
        if (dist < minDist) {
          minDist = dist;
          closest = brk;
        }
      }
      
      setSelected(closest);
    }
  }, [coords, selected]);

  // Fallback: set to first break if no coords available after a short delay
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!selected) {
        setSelected(surfbreaks[0]);
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [selected]);

  const distanceKm = selected && coords ? haversineKm(coords, selected) : null;
  const distanceMi = distanceKm !== null ? distanceKm * 0.621371 : null;
  const fmt = (n: number) => (n < 10 ? n.toFixed(1) : Math.round(n).toLocaleString());

  const openDirections = () => {
    if (!coords || !selected) return;
    const url = `https://www.google.com/maps/dir/?api=1&origin=${coords.lat},${coords.lon}&destination=${selected.lat},${selected.lon}&travelmode=driving`;
    window.open(url, "_blank", "noopener,noreferrer");
    setDirectionsOpen(false);
  };

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["conditions", "v2", selected?.id],
    queryFn: () => selected ? fetchConditions(selected) : Promise.reject("No location selected"),
    staleTime: 5 * 60 * 1000,
    retry: 2,
    retryDelay: (attempt) => attempt * 1500, // 1.5s then 3s
    enabled: !!selected,
  });

  useEffect(() => {
    if (selected) {
      document.title = `${selected.name} surf report — Barrelcast`;
    }
  }, [selected]);

  if (!selected) {
    return (
      <div className="flex flex-col min-h-screen bg-background items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="mt-3 text-sm text-muted-foreground">Finding closest break…</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background">

      {/* ── TOP BAR ── */}
      <header className="bg-[hsl(205,75%,14%)] border-b-2 border-[hsl(20,88%,46%)] sticky top-0 z-50">
        <div className="flex items-center justify-between px-4 py-3">

          {/* Break selector */}
          <button
            onClick={() => setPickerOpen(p => !p)}
            className="flex items-center gap-2 text-left"
          >
            <span className="w-2 h-2 rounded-full bg-[hsl(20,88%,46%)] animate-pulse shrink-0" />
            <div>
              <div className="font-display text-lg leading-tight text-[hsl(36,40%,96%)]">
                {selected.name}
                <ChevronDown className="inline h-4 w-4 ml-1 text-[hsl(20,88%,46%)]" />
              </div>
              <div className="text-[11px] text-[hsl(200,30%,60%)] font-light">
                {selected.region}, {selected.country} · {selected.waveType}
              </div>
            </div>
          </button>

          {/* Surfable stamp — reflects live conditions at this break */}
          {data?.current && (() => {
            const rating = conditionRating(data.current);
            const isSurfable = rating.label !== "Flat / Junky";
            return (
              <div
                className={
                  "flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest " +
                  (isSurfable
                    ? "bg-[hsl(20,88%,46%)] text-[hsl(36,40%,96%)]"
                    : "bg-[hsl(205,40%,22%)] text-[hsl(200,30%,60%)]")
                }
              >
                <Waves className="h-3 w-3" />
                {isSurfable ? "Surfable" : "Not Surfable"}
              </div>
            );
          })()}
        </div>

        {/* Break picker drawer */}
        {pickerOpen && (
          <div className="border-t border-[hsl(205,40%,22%)]">
            <BreakPicker
              selectedId={selected.id}
              onSelect={(b) => { setSelected(b); setPickerOpen(false); }}
              userCoords={coords}
              onRequestLocation={request}
              locating={locating}
              locationError={locationError}
              unit={unit}
              onUnitChange={setUnit}
            />
          </div>
        )}
      </header>

      {/* ── MAIN CONTENT ── */}
      <main className="flex-1 overflow-y-auto pb-40">

        {/* REPORT TAB */}
        {activeTab === "report" && (
          <>
            {isLoading && (
              <div className="flex flex-col items-center justify-center py-24 text-muted-foreground">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="mt-3 text-sm">Pulling live data for {selected.name}…</p>
              </div>
            )}

            {isError && (
              <div className="m-4 border-l-4 border-destructive bg-destructive/10 p-4 text-sm text-destructive space-y-2">
                <p className="font-semibold">Surf data unavailable right now</p>
                <p className="text-destructive/80 text-xs">
                  {(error as Error)?.message?.includes("502") || (error as Error)?.message?.includes("Bad Gateway")
                    ? "Open-Meteo is having a server outage — this isn't a problem with your app. Usually resolves in a few minutes."
                    : (error as Error)?.message ?? "Couldn't reach the marine API."}
                </p>
                <button
                  onClick={() => refetch()}
                  className="underline font-semibold text-destructive flex items-center gap-1"
                >
                  <Loader2 className={`h-3 w-3 ${isFetching ? "animate-spin" : "hidden"}`} />
                  Try again
                </button>
              </div>
            )}

            {data?.current && (
              <>
                {/* 1. BOARD REC — hero, above the fold */}
                <GearRecommender c={data.current} b={selected} />

                {/* 2. CONDITIONS — collapsed summary, expand for grid */}
                <div className="border-t border-border">
                  <button
                    onClick={() => setConditionsExpanded(p => !p)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-background"
                  >
                    <ConditionsSummary c={data.current} />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                      {conditionsExpanded ? "Less" : "Details"}
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform ${conditionsExpanded ? "rotate-180" : ""}`} />
                    </span>
                  </button>
                  {conditionsExpanded && <ConditionsPanel c={data.current} b={selected} />}
                </div>

                {/* 3. SUITABILITY + HOURLY */}
                <HourlyForecast hours={data.hourly} />

                {/* 4. FORECAST — PRO FEATURE */}
                <ProGate feature="extended_forecast">
                  <DailyForecast daily={data.daily} />
                </ProGate>

                {/* 5. ALERTS */}
                <ProGate feature="surf_alerts">
                  <SurfAlerts />
                </ProGate>

                {/* Distance / directions */}
                {distanceKm !== null && distanceMi !== null && (
                  <button
                    onClick={() => setDirectionsOpen(true)}
                    className="w-full flex items-center gap-2 px-4 py-3 border-t border-border text-sm bg-background"
                  >
                    <Navigation className="h-4 w-4 text-primary shrink-0" />
                    <span className="text-muted-foreground">{selected.name} is</span>
                    <span className="font-bold text-foreground">
                      {unit === "km" ? `${fmt(distanceKm)} km` : `${fmt(distanceMi)} mi`}
                    </span>
                    <span className="text-muted-foreground">from you</span>
                    <span className="ml-auto text-xs text-primary font-semibold">Directions →</span>
                  </button>
                )}

                {/* Data credit */}
                <p className="text-[11px] text-muted-foreground text-center py-4 border-t border-border">
                  Open-Meteo Marine & Forecast APIs ·{" "}
                  {isFetching ? "refreshing…" : `updated ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`}
                </p>
              </>
            )}
          </>
        )}

        {activeTab === "hotspots"  && <HotSpots />}
        {activeTab === "enso"      && <ElNinoMap />}
        {activeTab === "comps"     && <CompetitionsList />}
        {activeTab === "rankings"  && <Rankings />}
        {activeTab === "profile"   && <Profile />}
      </main>

      {/* ── BOTTOM TAB BAR ── */}
      <nav className="fixed bottom-0 inset-x-0 bg-[hsl(205,75%,14%)] border-t-2 border-[hsl(20,88%,46%)] z-50 flex">
        {TABS.map(({ v, icon: Icon, label }) => (
          <button
            key={v}
            onClick={() => setActiveTab(v)}
            className={`flex-1 flex flex-col items-center gap-1 py-2.5 text-[9px] font-bold uppercase tracking-wider transition-colors border-none bg-none ${
              activeTab === v
                ? "text-[hsl(20,88%,46%)]"
                : "text-[hsl(200,25%,45%)]"
            }`}
          >
            <Icon className="h-5 w-5" />
            {label}
          </button>
        ))}
      </nav>

      {/* Directions dialog */}
      <AlertDialog open={directionsOpen} onOpenChange={setDirectionsOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Open directions in Google Maps?</AlertDialogTitle>
            <AlertDialogDescription>
              We'll open a new tab with driving directions from your current location to{" "}
              {selected.name}
              {distanceKm !== null && (
                <> ({unit === "km" ? `${fmt(distanceKm)} km` : `${fmt(distanceMi!)} mi`} away)</>
              )}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={openDirections}>Open Google Maps</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Footer — only visible on non-report tabs */}
      {activeTab !== "report" && (
        <footer className="border-t border-border py-6 text-center text-xs text-muted-foreground pb-28">
          <p>Made with salt &amp; stoke. Always check local conditions before paddling out.</p>
          <p className="mt-1">© 2026 Barrelcast.</p>
          <div className="mt-2 flex justify-center gap-4">
            <a href="/terms" className="underline">Terms</a>
            <a href="/privacy" className="underline">Privacy</a>
            <a href="mailto:hello@barrelcast.surf" className="underline">hello@barrelcast.surf</a>
          </div>
        </footer>
      )}
    </div>
  );
};

// Inline conditions summary — single line, collapsed view
const ConditionsSummary = ({ c }: { c: any }) => {
  const parts = [
    c.waveHeight != null ? `${c.waveHeight.toFixed(1)}m` : null,
    c.wavePeriod != null ? `${c.wavePeriod.toFixed(1)}s` : null,
    c.windDirection && c.windSpeed != null ? `${c.windDirection} ${Math.round(c.windSpeed)}km/h` : null,
    c.seaTemp != null ? `${Math.round(c.seaTemp)}°C` : null,
  ].filter(Boolean);

  return (
    <span className="font-display text-sm text-foreground tracking-wide">
      {parts.join(" · ")}
    </span>
  );
};

export default Index;